// Insights data layer — «Инсайты о Вас» (spec §2.8, v2.5). The screens read
// the engine state through GET /engine/insights: no LLM, no report
// generator. Hide (§2.8 rule 8) removes a card forever. locked=true in the
// crisis mode closes the section; 409 means the user is not engine-enabled
// yet (pre-payment) — the screens render the subscription empty state.

import { useCallback, useEffect, useState } from 'react'
import { insightsApi, type InsightsScreen } from '../../../api/insights'
import type { InsightsResponse } from '../../../types/api'
import { toast } from '../../../store/toastStore'

export type InsightsPhase = 'loading' | 'ready' | 'locked' | 'no-subscription' | 'error'

// Session cache: tab switches between the five screens render instantly
// instead of flashing the spinner on every navigation (same pattern as the
// Practices catalog cache). Hide updates the cache in place.
let cached: InsightsResponse | null = null

export function __resetInsightsCacheForTests() {
  cached = null
}

export function useInsightsData() {
  const [phase, setPhase] = useState<InsightsPhase>(cached ? 'ready' : 'loading')
  const [data, setData] = useState<InsightsResponse | null>(cached)
  const [hiding, setHiding] = useState<string | null>(null)

  const load = useCallback(() => {
    setPhase('loading')
    return insightsApi
      .get()
      .then((payload) => {
        cached = payload
        setData(payload)
        setPhase(payload.locked ? 'locked' : 'ready')
      })
      .catch((err) => {
        setPhase(err.status === 409 ? 'no-subscription' : 'error')
      })
  }, [])

  useEffect(() => {
    if (!cached) load()
  }, [load])

  const hide = useCallback(async (kind: Exclude<InsightsScreen, 'progress'>, id: string) => {
    setHiding(`${kind}/${id}`)
    try {
      await insightsApi.hide(kind, id)
      if (cached) {
        cached = { ...cached, [kind]: (cached[kind] as Array<{ id: string }>).filter((c) => c.id !== id) } as InsightsResponse
      }
      setData((prev) => {
        if (!prev) return prev
        const next: InsightsResponse = { ...prev, [kind]: (prev[kind] as Array<{ id: string }>).filter((c) => c.id !== id) }
        if (kind === 'themes') next.notes = { ...prev.notes, themes_grows: next.themes.length < 2 }
        if (kind === 'strengths') next.notes = { ...prev.notes, strengths_grows: next.strengths.length < 2 }
        if (kind === 'self_view') next.notes = { ...prev.notes, self_view_grows: next.self_view.length < 2 }
        return next
      })
    } catch {
      toast('Не удалось скрыть карточку', 'error')
    } finally {
      setHiding(null)
    }
  }, [])

  return { phase, data, hiding, hide, retry: load }
}
